"use client"

import { Suspense, lazy, useState, useEffect, type ReactNode } from "react"
import { ErrorBoundary } from "@/components/error-boundary"

// Lazy load the 3D components for better performance
const FloatingShapesCanvas = lazy(() =>
  import("./floating-shapes").then((mod) => ({
    default: mod.FloatingShapesCanvas,
  }))
)

interface ThreeCanvasProps {
  className?: string
  fallback?: ReactNode
}

// Loading placeholder
function CanvasLoader() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[var(--color-accent-gold)] border-t-transparent rounded-full animate-spin opacity-30" />
    </div>
  )
}

// Gradient fallback when 3D fails
function GradientFallback({ className }: { className?: string }) {
  return (
    <div
      className={`absolute inset-0 ${className || ""}`}
      style={{
        background: `
          radial-gradient(ellipse 80% 60% at 30% 20%, rgba(201, 162, 39, 0.08) 0%, transparent 50%),
          radial-gradient(ellipse 70% 50% at 70% 80%, rgba(42, 157, 143, 0.06) 0%, transparent 50%)
        `,
      }}
    />
  )
}

// Main wrapper that handles SSR, loading states, and errors
export function ThreeCanvas({ className, fallback }: ThreeCanvasProps) {
  const [isMounted, setIsMounted] = useState(false)
  const [isSupported, setIsSupported] = useState(true)

  useEffect(() => {
    setIsMounted(true)

    // Check WebGL support
    try {
      const canvas = document.createElement("canvas")
      const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl")
      if (!gl) {
        setIsSupported(false)
      }
    } catch {
      setIsSupported(false)
    }

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (prefersReducedMotion) {
      setIsSupported(false)
    }
  }, [])

  // Don't render on server or if WebGL not supported
  if (!isMounted || !isSupported) {
    return fallback || <GradientFallback className={className} />
  }

  return (
    <ErrorBoundary
      fallback={fallback || <GradientFallback className={className} />}
      onError={(error) => {
        console.warn("Three.js rendering error, falling back to gradient:", error.message)
      }}
    >
      <Suspense fallback={fallback || <CanvasLoader />}>
        <FloatingShapesCanvas className={className} />
      </Suspense>
    </ErrorBoundary>
  )
}
